import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Template } from "@/types/template";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { TemplateContent } from "@/components/template/TemplateContent";
import { processUploadedTemplate } from "@/utils/templateProcessor";
import { Download, ChevronLeft, Save, MessageSquare, Wand2 } from "lucide-react";
import Editor from "@monaco-editor/react";
import "@/styles/TemplateDocument.css";
import * as prettier from "prettier/standalone";
import * as prettierPluginHtml from "prettier/plugins/html";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export default function ReviewDocument() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [comments, setComments] = useState<string>("");
  const [addToCompanyLibrary, setAddToCompanyLibrary] = useState(false);
  const [processedContent, setProcessedContent] = useState<{
    processedHtml: string;
    placeholders: string[];
    displayNames: Record<string, string>;
  } | null>(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [isEdited, setIsEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [editorInstance, setEditorInstance] = useState<any>(null);

  // Format HTML using Prettier standalone with HTML plugin
  const formatHtml = async (html: string): Promise<string> => {
    try {
      const formatted = await prettier.format(html, {
        parser: "html",
        plugins: [prettierPluginHtml],
        printWidth: 100,
        tabWidth: 2,
        useTabs: false,
        htmlWhitespaceSensitivity: "css",
        bracketSameLine: false,
        singleAttributePerLine: false,
      });
      return formatted;
    } catch (error) {
      console.error('Error formatting HTML with Prettier:', error);
      // Fallback to simple formatting if Prettier fails
      return html
        .replace(/>\s+</g, '><')
        .replace(/></g, '>\n<')
        .split('\n')
        .map((line, index) => {
          const trimmed = line.trim();
          if (!trimmed) return '';
          
          // Simple indentation based on nesting level
          const openTags = (trimmed.match(/<(?!\/)[^>]*>/g) || []).length;
          const closeTags = (trimmed.match(/<\/[^>]*>/g) || []).length;
          const netTags = openTags - closeTags;
          
          // Calculate indentation based on previous lines
          let indentLevel = 0;
          if (trimmed.startsWith('</')) {
            indentLevel = Math.max(0, Math.floor(index / 3) - 1);
          } else if (trimmed.startsWith('<') && !trimmed.endsWith('/>')) {
            indentLevel = Math.floor(index / 3);
          } else {
            indentLevel = Math.floor(index / 3);
          }
          
          return '  '.repeat(indentLevel) + trimmed;
        })
        .filter(line => line.length > 0)
        .join('\n');
    }
  };

  const handleFormatHtml = async () => {
    if (editorInstance && htmlContent) {
      try {
        const formatted = await formatHtml(htmlContent);
        setHtmlContent(formatted);
        editorInstance.setValue(formatted);
        setIsEdited(true);
        
        // Update processed content
        try {
          const processed = processUploadedTemplate(formatted);
          setProcessedContent(processed);
        } catch (error) {
          console.error('Error processing formatted HTML:', error);
        }
      } catch (error) {
        console.error('Error formatting HTML:', error);
        toast({
          title: "Error",
          description: "Failed to format HTML",
          variant: "destructive",
        });
      }
    }
  };

  useEffect(() => {
    if (!id) return;

    const fetchTemplate = async () => {
      try {
        const { data, error } = await supabase
          .from("templates")
          .select("*")
          .eq("id", id)
          .single();

        if (error) {
          throw error;
        }

        if (!data) {
          toast({
            title: "Error",
            description: "Template not found",
            variant: "destructive",
          });
          navigate("/admin/review");
          return;
        }

        setTemplate(data as Template);
        setComments(data.comments || "");
        setAddToCompanyLibrary(!!data.is_company_library);

        // Fetch the actual HTML content
        if (data.processed_html) {
          const { data: fileData, error: downloadError } = await supabase.storage
            .from('template_html')
            .download(data.processed_html);

          if (downloadError) {
            throw new Error(`Failed to download template: ${downloadError.message}`);
          }

          if (!fileData) {
            throw new Error('No file data received');
          }

          // Read the file content
          const htmlContent = await fileData.text();
          setHtmlContent(htmlContent);
          
          // Process the template using the same logic as user edit
          const processed = processUploadedTemplate(htmlContent);
          setProcessedContent(processed);
        } else if (data.admin_uploaded_html) {
          const { data: fileData, error: downloadError } = await supabase.storage
            .from('template_html')
            .download(data.admin_uploaded_html);

          if (downloadError) {
            throw new Error(`Failed to download template: ${downloadError.message}`);
          }

          if (!fileData) {
            throw new Error('No file data received');
          }

          // Read the file content
          const htmlContent = await fileData.text();
          setHtmlContent(htmlContent);
          
          // Process the template using the same logic as user edit
          const processed = processUploadedTemplate(htmlContent);
          setProcessedContent(processed);
        } else {
          toast({
            title: "Warning",
            description: "No HTML content available for this template",
            variant: "destructive",
          });
        }
        
        setLoading(false);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch template",
          variant: "destructive",
        });
        navigate("/admin/review");
      }
    };

    fetchTemplate();
  }, [id, navigate, toast]);

  const handleApprove = async () => {
    if (!template || !id) return;

    setApproving(true);
    try {
      // If the HTML was edited, upload the new version
      let updatedHtmlPath = template.processed_html;
      if (isEdited && htmlContent) {
        // Use the correct folder structure based on whether it's going to company library
        let fileName: string;
        if (addToCompanyLibrary) {
          // If going to company library, create a dedicated company folder
          fileName = `company_library/admin_edited_${template.id}_${Date.now()}.html`;
        } else {
          // If staying with user, use the original user's folder
          fileName = `${template.user_id}/admin_edited_${template.id}_${Date.now()}.html`;
        }
        
        const { error: uploadError } = await supabase.storage
          .from('template_html')
          .upload(fileName, new Blob([htmlContent], { type: 'text/html' }), {
            upsert: true,
          });

        if (uploadError) throw uploadError;
        updatedHtmlPath = fileName;
      }

      const { error } = await supabase
        .from("templates")
        .update({
          admin_approval_status: "approved",
          is_approved: true,
          needs_review: false,
          comments,
          is_company_library: addToCompanyLibrary,
          processed_html: updatedHtmlPath,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Template approved successfully",
      });
      navigate("/admin/review");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to approve template",
        variant: "destructive",
      });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!template || !id) return;

    setRejecting(true);
    try {
      const { error } = await supabase
        .from("templates")
        .update({
          admin_approval_status: "rejected",
          is_approved: false,
          needs_review: false,
          comments,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Template rejected successfully",
      });
      navigate("/admin/review");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject template",
        variant: "destructive",
      });
    } finally {
      setRejecting(false);
    }
  };

  const handleDownloadHtml = async () => {
    if (!template || !processedContent) return;

    try {
      // Create a Blob from the processed HTML
      const blob = new Blob([processedContent.processedHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.filename.replace(/\.[^/.]+$/, "")}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download HTML",
        variant: "destructive",
      });
    }
  };

  const handleHtmlChange = (value: string | undefined) => {
    if (value !== undefined) {
      setHtmlContent(value);
      setIsEdited(true);
      try {
        const processed = processUploadedTemplate(value);
        setProcessedContent(processed);
      } catch (error) {
        console.error('Error processing updated HTML:', error);
      }
    }
  };

  const handleSaveDraft = async () => {
    if (!template || !id || !htmlContent) return;

    setSaving(true);
    try {
      // Use the existing HTML path or create a new one if it doesn't exist
      const htmlPath = template.processed_html || template.admin_uploaded_html || `${template.user_id}/draft_${template.id}_${Date.now()}.html`;

      // Upload the HTML content
      const { error: uploadError } = await supabase.storage
        .from('template_html')
        .upload(htmlPath, new Blob([htmlContent], { type: 'text/html' }), {
          upsert: true, // This will overwrite if the file exists
        });

      if (uploadError) throw uploadError;

      // Update the template record with the new HTML path
      const { error: updateError } = await supabase
        .from("templates")
        .update({
          processed_html: htmlPath,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Draft saved successfully",
      });
      
      // Update local template state
      setTemplate(prev => prev ? {
        ...prev,
        processed_html: htmlPath
      } : null);
      
      setIsEdited(false); // Reset edited state since we just saved
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save draft",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Fixed Header */}
      <div className="border-b bg-background">
        <div className="container py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/admin/review")}
                className="flex items-center"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back to Review Queue
              </Button>
              <h2 className="text-lg font-semibold">
                {template?.filename || "Unnamed Template"}
              </h2>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowCommentsDialog(true)}
                className="flex items-center"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                Review Comments
              </Button>
              <Button
                variant="outline"
                onClick={handleFormatHtml}
                disabled={!htmlContent || !editorInstance}
                className="flex items-center"
              >
                <Wand2 className="mr-2 h-4 w-4" />
                Format HTML
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={saving || !isEdited}
                className="flex items-center"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? "Saving..." : "Save Draft"}
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadHtml}
                disabled={!processedContent}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={approving || rejecting}
              >
                {rejecting ? "Rejecting..." : "Reject"}
              </Button>
              <Button
                onClick={() => setShowReviewDialog(true)}
                disabled={approving || rejecting}
              >
                {approving ? "Approving..." : "Approve"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area – live side-by-side editor */}
      <div className="flex flex-1 overflow-hidden">
        {/* HTML Editor */}
        <div className="w-1/2 h-full border-r">
                <Editor
                  height="100%"
                  defaultLanguage="html"
                  value={htmlContent}
                  onChange={handleHtmlChange}
                  options={{
                    minimap: { enabled: true },
                    fontSize: 14,
                    wordWrap: "on",
                    formatOnPaste: true,
                    formatOnType: true,
                    automaticLayout: true,
                  }}
                  theme="vs-light"
            onMount={(editor) => {
              setEditorInstance(editor);
            }}
          />
        </div>

        {/* Live Preview */}
        <div className="flex-1 h-full overflow-auto p-4 bg-background">
          {processedContent && (
            <div className="template-page bg-white">
              <TemplateContent
                content={processedContent.processedHtml}
                displayAsForm={false}
                className="template-content"
                />
              </div>
          )}
          </div>
      </div>

      {/* Review Comments Dialog */}
      <Dialog open={showCommentsDialog} onOpenChange={setShowCommentsDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Review Comments</DialogTitle>
            <DialogDescription>
              Add your review comments and settings for this template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="comments">Comments</Label>
              <Textarea
                id="comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add your review comments here..."
                className="min-h-[150px]"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="company-library"
                checked={addToCompanyLibrary}
                onCheckedChange={(checked) => setAddToCompanyLibrary(checked as boolean)}
              />
              <Label htmlFor="company-library">Add to Company Library</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommentsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Template Approval</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this template? This will make it
              available to users.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={approving}>
              {approving ? "Approving..." : "Confirm Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
